<template>
    <pendo-multiselect
        v-if="isLocalizationEnabled"
        :class="`language-chooser--${theme}`"
        :value="selectedOption"
        :full-width="fullWidth"
        :options="languageOptions"
        :loading="isLoading"
        :popper-options="{ class: `language-chooser-panel--${theme}` }"
        placeholder="Select Language"
        value-key="code"
        label-key="name"
        @select="setLanguage">
        <template #header="{ select }">
            <div class="language-filter__header">
                <div
                    class="language-filter__all-languages-option"
                    :class="{ 'is-selected': selectedOption.code === '*' }"
                    @click="select(allLanguages)">
                    All Languages
                </div>
            </div>
        </template>
    </pendo-multiselect>
</template>

<script>
import isUndefined from 'lodash/isUndefined';
import { formatLanguages } from '@/settings/localization-settings/guide-localization.service.js';
import { PendoMultiselect } from '@pendo/components';
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
    name: 'LanguageFilter',
    components: {
        PendoMultiselect,
    },
    props: {
        appId: {
            type: [String, Number],
            default: '*',
        },
        value: {
            type: String,
            default: '*',
        },
        theme: {
            type: String,
            default: 'light',
        },
        config: {
            type: Object,
            default: () => ({}),
        },
        fullWidth: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['change'],
    data () {
        return {
            isLoading: false,
            allLanguages: { code: '*', name: 'All Languages' },
        };
    },
    computed: {
        ...mapState({
            localizationDataMap: (state) => state.localization.map,
        }),
        ...mapGetters({
            isAppLocalizationEnabled: 'apps/isAppLocalizationEnabled',
        }),
        selectedOption () {
            const selectedLanguageOption = this.languageOptions.find(({ code }) => code === this.value);

            return selectedLanguageOption || this.allLanguages;
        },
        languageOptions () {
            if (this.localizationDataMap[this.appId]) {
                return formatLanguages(this.localizationDataMap[this.appId]).filter((language) =>
                    this.includeLanguage(language),
                );
            }

            return [];
        },
        isLocalizationEnabled () {
            return this.isAppLocalizationEnabled(this.appId);
        },
    },
    watch: {
        appId: {
            async handler () {
                if (isUndefined(this.appId)) return;
                this.isLoading = true;
                await this.loadOne({ appId: this.appId });
                this.resetSelectedLanguage();
                this.isLoading = false;
            },
            immediate: true,
        },
    },
    methods: {
        ...mapActions('localization', ['loadOne']),
        resetSelectedLanguage () {
            const { code } = this.languageOptions.find(({ code }) => code === this.value) || {};

            if (!this.isLocalizationEnabled || !code) {
                this.setLanguage({ code: '*' });
            }
        },
        setLanguage (lang) {
            this.$emit('change', lang);
        },
        includeLanguage ({ isRtl }) {
            if (this.config?.ignoreRtlLanguages) {
                return !isRtl;
            }

            return true;
        },
    },
};
</script>

<style scoped lang="less">
.language-chooser-panel--dark {
    .pendo-picker-popper__content {
        border-color: var(--palette-gray-100);
    }
}

.language-chooser--dark {
    .pendo-multiselect__trigger {
        border-color: var(--palette-gray-100);
    }
}

.language-filter__header {
    height: 40px;
    border-bottom: 1px solid var(--palette-gray-30);
    display: flex;
    align-items: center;

    .language-filter__all-languages-option {
        padding: 0 16px;
        height: 26px;
        flex: 1;
        line-height: 26px;
        cursor: pointer;

        &:hover {
            background-color: var(--palette-gray-20);
        }

        &.is-selected {
            background-color: var(--palette-gray-30);
        }
    }
}
</style>
